import { create } from 'zustand';
import { CSVFile, CSVUploadState, UploadStep, ValidationResult, Location } from '../types/data/csv';
import csvService from '../services/csvService';

// Re-export UploadStep
export { UploadStep };

export interface ValidationError {
  row: number;
  column: string;
  message: string;
}

export interface ValidationWarning {
  row: number;
  column: string;
  message: string;
}

export interface ValidationResults {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface DateRange {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
}

export type SubmissionMode = 'single-period' | 'multi-period';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  parsedData: { headers: string[]; rows: any[][] };
  isValid: boolean;
  csvFile: CSVFile;
}

export interface ColumnConflict {
  columnName: string;
  files: Array<{
    fileId: string;
    fileName: string;
  }>;
}

interface CSVState {
  csvData: string[][];
  isLoading: boolean;
  error: string | null;
  validationResults: ValidationResults | null;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  uploadedFile: CSVFile | null;
  
  // Multi-file support
  uploadedFiles: UploadedFile[];
  selectedFileId: string | null;
  columnConflicts: ColumnConflict[];
  
  currentStep: UploadStep;
  selectedLocation: Location | null;
  accessibleLocations: Location[];
  isMultiLocationMode: boolean;
  
  // NEW: Multi-period support
  submissionMode: SubmissionMode;
  isMultiPeriodMode: boolean;
  detectedDateRange: DateRange | null;
  
  // Actions
  setCSVData: (data: string[][]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setValidationResults: (results: ValidationResults | null) => void;
  setUploadedFile: (file: CSVFile | null) => void;
  
  // Multi-file actions
  addUploadedFile: (uploadedFile: UploadedFile) => void;
  removeUploadedFile: (fileId: string) => void;
  setSelectedFileId: (fileId: string | null) => void;
  setColumnConflicts: (conflicts: ColumnConflict[]) => void;
  resolveColumnConflict: (columnName: string, selectedFileId: string) => void;
  getAllUniqueColumns: () => string[];
  getFilesContainingColumn: (columnName: string) => UploadedFile[];
  updateColumnConflicts: () => void;
  
  clearData: () => void;
  setCurrentStep: (step: UploadStep) => void;
  setSelectedLocation: (location: Location | null) => void;
  setAccessibleLocations: (locations: Location[]) => void;
  setMultiLocationMode: (isMultiLocation: boolean) => void;
  
  // NEW: Multi-period actions
  setSubmissionMode: (mode: SubmissionMode) => void;
  setMultiPeriodMode: (isMultiPeriod: boolean) => void;
  setDetectedDateRange: (dateRange: DateRange | null) => void;
  
  reset: () => void;
  validateCurrentFile: () => void;
}

export const useCSVStore = create<CSVState>((set, get) => ({
  csvData: [],
  isLoading: false,
  error: null,
  validationResults: null,
  errors: [],
  warnings: [],
  uploadedFile: null,
  
  // Multi-file defaults
  uploadedFiles: [],
  selectedFileId: null,
  columnConflicts: [],
  
  currentStep: UploadStep.SELECT_FILE,
  selectedLocation: null,
  accessibleLocations: [],
  isMultiLocationMode: false,
  
  // NEW: Multi-period defaults
  submissionMode: 'single-period',
  isMultiPeriodMode: false,
  detectedDateRange: null,
  
  setCSVData: (data) => set({ csvData: data }),
  
  setIsLoading: (isLoading) => set({ isLoading }),
  
  setError: (error) => set({ error }),
  
  setValidationResults: (results) => set({
    validationResults: results,
    errors: results?.errors || [],
    warnings: results?.warnings || []
  }),
  
  setUploadedFile: (file) => set({ uploadedFile: file }),
  
  // Multi-file actions
  addUploadedFile: (uploadedFile) => {
    const state = get();
    const existingIndex = state.uploadedFiles.findIndex(f => f.id === uploadedFile.id);
    
    if (existingIndex >= 0) {
      // Replace existing file
      const newFiles = [...state.uploadedFiles];
      newFiles[existingIndex] = uploadedFile;
      set({ uploadedFiles: newFiles });
    } else {
      // Add new file
      set({ uploadedFiles: [...state.uploadedFiles, uploadedFile] });
    }
    
    // Auto-select first file if none selected
    if (!state.selectedFileId) {
      set({ selectedFileId: uploadedFile.id });
    }
    
    // Update column conflicts
    get().updateColumnConflicts();
  },
  
  removeUploadedFile: (fileId) => {
    const state = get();
    const newFiles = state.uploadedFiles.filter(f => f.id !== fileId);
    set({ 
      uploadedFiles: newFiles,
      selectedFileId: state.selectedFileId === fileId ? (newFiles[0]?.id || null) : state.selectedFileId
    });
    
    // Update column conflicts
    get().updateColumnConflicts();
  },
  
  setSelectedFileId: (fileId) => set({ selectedFileId: fileId }),
  
  setColumnConflicts: (conflicts) => set({ columnConflicts: conflicts }),
  
  resolveColumnConflict: (columnName, selectedFileId) => {
    // This will be used when user selects which file to use for a conflicted column
    // Implementation will be added when we build the conflict resolution UI
    console.log(`Resolving conflict for column '${columnName}' with file '${selectedFileId}'`);
  },
  
  getAllUniqueColumns: () => {
    const state = get();
    const allColumns = new Set<string>();
    
    state.uploadedFiles.forEach(file => {
      file.parsedData.headers.forEach(header => {
        allColumns.add(header.trim());
      });
    });
    
    return Array.from(allColumns).sort();
  },
  
  getFilesContainingColumn: (columnName) => {
    const state = get();
    return state.uploadedFiles.filter(file => 
      file.parsedData.headers.some(header => 
        header.trim().toLowerCase() === columnName.trim().toLowerCase()
      )
    );
  },
  
  updateColumnConflicts: () => {
    const state = get();
    const columnFileMap = new Map<string, UploadedFile[]>();
    
    // Define standard columns that should exist in every file and are not conflicts
    const standardColumns = new Set([
      'dealer', 'location', 'site', 'branch', // Location identifiers
      'year', 'month', 'period', 'date',      // Time identifiers
      'organization', 'org', 'company'        // Organization identifiers
    ]);
    
    // Group files by column names
    state.uploadedFiles.forEach(file => {
      file.parsedData.headers.forEach(header => {
        const normalizedHeader = header.trim().toLowerCase();
        if (!columnFileMap.has(normalizedHeader)) {
          columnFileMap.set(normalizedHeader, []);
        }
        columnFileMap.get(normalizedHeader)!.push(file);
      });
    });
    
    // Find conflicts (columns that exist in multiple files, excluding standard columns)
    const conflicts: ColumnConflict[] = [];
    columnFileMap.forEach((files, columnName) => {
      if (files.length > 1 && !standardColumns.has(columnName)) {
        conflicts.push({
          columnName: files[0].parsedData.headers.find(h => 
            h.trim().toLowerCase() === columnName
          ) || columnName,
          files: files.map(f => ({ 
            fileId: f.id, 
            fileName: f.name,
            sampleData: files[0].parsedData.rows.slice(0, 3).map(row => {
              const headerIndex = files[0].parsedData.headers.findIndex(h => 
                h.trim().toLowerCase() === columnName
              );
              return headerIndex >= 0 ? String(row[headerIndex] || '') : '';
            })
          }))
        });
      }
    });
    
    set({ columnConflicts: conflicts });
  },
  
  clearData: () => set({
    csvData: [],
    error: null,
    validationResults: null,
    errors: [],
    warnings: [],
    uploadedFile: null,
    // Clear multi-file data
    uploadedFiles: [],
    selectedFileId: null,
    columnConflicts: []
  }),
  
  setCurrentStep: (step) => set({ currentStep: step }),
  
  setSelectedLocation: (location) => set({ selectedLocation: location }),
  
  setAccessibleLocations: (locations) => set({ 
    accessibleLocations: locations,
    // Only auto-select if there's exactly one location
    selectedLocation: locations.length === 1 ? locations[0] : null
  }),
  
  setMultiLocationMode: (isMultiLocation) => set({ 
    isMultiLocationMode: isMultiLocation,
    // Clear selected location when switching to multi-location mode
    selectedLocation: isMultiLocation ? null : get().selectedLocation
  }),
  
  // NEW: Multi-period actions
  setSubmissionMode: (mode) => set({ 
    submissionMode: mode,
    isMultiPeriodMode: mode === 'multi-period'
  }),
  
  setMultiPeriodMode: (isMultiPeriod) => set({ 
    isMultiPeriodMode: isMultiPeriod,
    submissionMode: isMultiPeriod ? 'multi-period' : 'single-period',
    // Clear detected date range when switching modes
    detectedDateRange: isMultiPeriod ? get().detectedDateRange : null
  }),
  
  setDetectedDateRange: (dateRange) => set({ detectedDateRange: dateRange }),
  
  reset: () => set({ 
    csvData: [], 
    isLoading: false, 
    error: null, 
    validationResults: null,
    errors: [],
    warnings: [],
    uploadedFile: null,
    // Reset multi-file state
    uploadedFiles: [],
    selectedFileId: null,
    columnConflicts: [],
    currentStep: UploadStep.SELECT_FILE,
    selectedLocation: null,
    accessibleLocations: [],
    isMultiLocationMode: false,
    // Reset multi-period state
    submissionMode: 'single-period',
    isMultiPeriodMode: false,
    detectedDateRange: null
  }),
  
  validateCurrentFile: () => {
    const { uploadedFile, csvData } = get();
    if (!uploadedFile || csvData.length === 0) return;
    
    // Skip heavy validation for performance - just mark as valid
    const validationResults: ValidationResults = {
      valid: true,
      errors: [],
      warnings: []
    };
    
    set({ 
      validationResults,
      errors: [],
      warnings: []
    });
    
    // Move to next step
    set({ currentStep: UploadStep.REVIEW_DATA });
  }
})); 